import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import WTypography from '../../../Components/whiteTypography'
import ConseillerChat from '../../ConseillerPatrimonial/components/ConseillerChat'
import { loadWallet } from '../../../Components/Billing/billingThunks'
import { makePostRequest } from 'utils/api'

import { buildProAdvisor } from '../utils/buildProAdvisor'
import { fetchProAdvisorSources } from '../utils/fetchProAdvisorSources'
import {
  ProScoreCard,
  ProDiagnosticPanel,
  ProActionsPanel,
  ProBreakdownPanel,
  ProRecommendationPanel,
} from './ProAdvisorSections'

/**
 * LAP-767 — Page principale "Mon conseiller pro / formation IA".
 *
 * Pattern : strict miroir de `ConseillerEcologiePage` / `ConseillerTempsPage`.
 *   - On agrège les cartes de /api/Professional/getProfessionnelle/
 *     (profil / compétences / soft skills / formation / opportunités).
 *   - On passe l'agrégat à `buildProAdvisor` qui produit score / diagnostic /
 *     actions / répartition / recommandations.
 *   - On expose un chat (réutilisation directe de `ConseillerChat`) pour les
 *     questions libres.
 *
 * Route : /conseiller-pro (cf. App.js).
 */
function ConseillerProPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation('layout')

  // LAP — carte SmartFeed "le mot du conseiller pro" : seedPrompt transmis
  // par goToButtonTarget depuis la carte du fil, auto-envoyé au chat
  // (même pattern que ConseillerTempsPage / ConseillerPatrimonialPage).
  const [autoSendPrompt] = useState(() => (location.state?.seedPrompt || '').trim())

  const [chatOpen, setChatOpen] = useState(true)
  const [snackbar, setSnackbar] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(new Date())

  const [rawSources, setRawSources] = useState(null)
  const siteUser = useSelector((s) => s.user)

  // Recalcul de l'advisor à chaque changement de sources OU de langue.
  const advisorData = useMemo(() => {
    if (!rawSources) return null
    return buildProAdvisor({ ...rawSources, t })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSources, t, i18n.language])

  const displayedFirstName =
    advisorData?.userProfile?.firstName ||
    siteUser?.user?.first_name ||
    siteUser?.user?.firstName ||
    siteUser?.user?.prenom ||
    ''

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    fetchAllSources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * LAP-767 — Agrégation des sources Life du conseiller pro via
   * `fetchProAdvisorSources` (endpoints isolés, jamais de throw). Si une source
   * manque, l'advisor produit un diagnostic partiel mais exploitable.
   */
  const fetchAllSources = async ({ forceRefresh = false } = {}) => {
    if (forceRefresh) setRegenerating(true)
    else setInitialLoading(true)

    try {
      const sources = await fetchProAdvisorSources(siteUser?.user || {})
      setRawSources(sources)
      setLastUpdate(new Date())

      if (forceRefresh) {
        try {
          await dispatch(loadWallet())
        } catch (_) {
          /* silencieux */
        }
        setSnackbar({
          severity: 'success',
          message: t('conseillerPro.regenerateSuccess', {
            defaultValue: 'Analyse professionnelle actualisée.',
          }),
        })
      }
    } catch (e) {
      console.warn('[ConseillerPro] fetch error', e?.message)
      if (!rawSources) {
        setRawSources({ user: siteUser?.user || {}, cards: [], advice: {} })
      }
      if (forceRefresh) {
        setSnackbar({
          severity: 'error',
          message: t('conseillerPro.regenerateError', {
            defaultValue: 'Impossible d’actualiser l’analyse pour le moment.',
          }),
        })
      }
    } finally {
      if (forceRefresh) setRegenerating(false)
      else setInitialLoading(false)
    }
  }

  const handleRegenerate = () => {
    if (regenerating || initialLoading) return
    fetchAllSources({ forceRefresh: true })
  }

  /**
   * LAP-767 — Envoi d'un message au conseiller pro.
   * CONTRAT BACKEND attendu :
   *   POST /api/Professional/pro-advisor/chat/
   *     body: { message, locale, feature: 'pro_chat', context }
   *     output: { reply, source, locale, generated_at }
   * Tant que l'endpoint n'existe pas, on relaie le rejet à ConseillerChat qui
   * utilise sa réponse de démo.
   */
  const handleChatSend = async (message, locale) => {
    try {
      const response = await makePostRequest(
        '/api/Professional/pro-advisor/chat/',
        {
          message,
          locale,
          feature: 'pro_chat',
          context: advisorData
            ? {
                totals: advisorData.totals,
                score: advisorData.proScore.global,
                topRisks: advisorData.diagnostic.risks.map((r) => r.key),
              }
            : null,
        },
        {},
        true,
      )
      const data = response?.data || {}
      try {
        await dispatch(loadWallet())
      } catch (_) {
        /* silencieux */
      }
      return {
        role: 'agent',
        content: data.reply || data.content || data.text || '',
        source: data.source || t('dashboard.lifeAgentSignature'),
        timestamp: data.generated_at || new Date().toISOString(),
        locale: data.locale || locale,
      }
    } catch (e) {
      console.warn('[ConseillerPro] endpoint chat indispo, fallback démo', e?.message)
      throw e
    }
  }

  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #050b18 0%, #0a162e 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
          px: 4,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#5b9bff' }} />
        <Box>
          <WTypography variant="h6" sx={{ fontWeight: 700 }}>
            {t('conseillerPro.initialLoading', {
              defaultValue: 'Votre conseiller analyse votre profil professionnel…',
            })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#9bbcf0 !important', mt: 1, maxWidth: 480 }}>
            {t('conseillerPro.initialLoadingHint', {
              defaultValue:
                'On agrège votre cap professionnel, vos compétences, vos soft skills, votre formation continue et vos opportunités.',
            })}
          </WTypography>
        </Box>
      </Box>
    )
  }

  const initialChatMessages = [
    {
      role: 'agent',
      content: t('conseillerPro.chat.welcome', {
        defaultValue: `Bonjour ${displayedFirstName || ''} 👔 Je suis votre conseiller pro & formation. Posez-moi n’importe quelle question sur votre carrière, vos compétences et les formations qui vous feraient progresser.`,
        name: displayedFirstName,
      }),
      source: t('dashboard.lifeAgentSignature'),
      timestamp: new Date().toISOString(),
    },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #050b18 0%, #0a162e 100%)',
        color: 'white',
        pb: 6,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(12px)',
          bgcolor: 'rgba(5,11,24,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            onClick={() => navigate('/professionnelle')}
            sx={{ color: '#d2e2fb', flexShrink: 0 }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box flex={1} minWidth={0}>
            <WTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: 14, sm: 16, md: 20 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('conseillerPro.pageTitle', {
                defaultValue: 'Mon conseiller pro & formation',
              })}
            </WTypography>
            <WTypography
              variant="caption"
              sx={{ color: '#9bbcf0 !important', display: { xs: 'none', md: 'block' } }}
            >
              {t('conseillerPro.lastUpdated', {
                defaultValue: `Mis à jour ${formatRelativeI18n(lastUpdate, t)}`,
                date: formatRelativeI18n(lastUpdate, t),
              })}
            </WTypography>
          </Box>

          <IconButton
            onClick={handleRegenerate}
            disabled={regenerating}
            sx={{
              color: '#d2e2fb',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              '&.Mui-disabled': { color: 'rgba(210,226,251,0.4)' },
            }}
            aria-label={t('conseillerPro.regenerate', {
              defaultValue: 'Actualiser l’analyse',
            })}
            title={t('conseillerPro.regenerate', {
              defaultValue: 'Actualiser l’analyse',
            })}
          >
            <RestartAltIcon
              sx={{
                animation: regenerating ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>

          <IconButton
            onClick={() => setChatOpen(!chatOpen)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              flexShrink: 0,
              bgcolor: chatOpen ? '#5b9bff' : 'transparent',
              color: chatOpen ? '#050b18' : '#d2e2fb',
              border: chatOpen ? 'none' : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: chatOpen ? '#4a86e8' : 'rgba(255,255,255,0.06)',
              },
            }}
            aria-label={
              chatOpen
                ? t('conseillerPro.hideChat', { defaultValue: 'Masquer le chat' })
                : t('conseillerPro.openChat', { defaultValue: 'Ouvrir le chat' })
            }
          >
            <ChatBubbleOutlineIcon />
          </IconButton>

          <Button
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={() => setChatOpen(!chatOpen)}
            variant={chatOpen ? 'contained' : 'outlined'}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              textTransform: 'none',
              fontWeight: 600,
              flexShrink: 0,
              ...(chatOpen
                ? { bgcolor: '#5b9bff', color: '#050b18', '&:hover': { bgcolor: '#4a86e8' } }
                : { color: '#d2e2fb', borderColor: 'rgba(255,255,255,0.2)' }),
            }}
          >
            {chatOpen
              ? t('conseillerPro.hideChat', { defaultValue: 'Masquer le chat' })
              : t('conseillerPro.openChat', { defaultValue: 'Ouvrir le chat' })}
          </Button>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pt: 3 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            lg: chatOpen ? '2fr 1fr' : '1fr',
          }}
          gap={3}
          alignItems="flex-start"
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {advisorData && (
              <>
                <ProScoreCard
                  score={advisorData.proScore}
                  totals={advisorData.totals}
                  userFirstName={displayedFirstName}
                />
                <ProRecommendationPanel recommendation={advisorData.recommendation} />
                <ProDiagnosticPanel
                  diagnostic={advisorData.diagnostic}
                  recommendations={advisorData.recommendations}
                />
                <ProBreakdownPanel breakdown={advisorData.breakdown} />
                <ProActionsPanel actions={advisorData.actionsPrioritaires} />
              </>
            )}
          </Box>

          {chatOpen && (
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 90 },
                alignSelf: 'flex-start',
                minWidth: 0,
              }}
            >
              <ConseillerChat
                messages={initialChatMessages}
                suggestedPrompts={advisorData?.suggestedPrompts || []}
                userLocale={i18n.language || 'fr-FR'}
                agentSignature={t('dashboard.lifeAgentSignature')}
                onSend={handleChatSend}
                autoSendPrompt={autoSendPrompt}
              />
            </Box>
          )}
        </Box>
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

function formatRelativeI18n(date, t) {
  if (!date) return ''
  const now = Date.now()
  const then = date instanceof Date ? date.getTime() : new Date(date).getTime()
  const diff = Math.max(0, now - then)
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 1)
    return t('conseillerPro.relativeTime.justNow', { defaultValue: 'à l’instant' })
  if (minutes < 60)
    return t('conseillerPro.relativeTime.minutesAgo', {
      defaultValue: `il y a ${minutes} min`,
      n: minutes,
    })
  if (hours < 24)
    return t('conseillerPro.relativeTime.hoursAgo', {
      defaultValue: `il y a ${hours} h`,
      n: hours,
    })
  return t('conseillerPro.relativeTime.daysAgo', {
    defaultValue: `il y a ${days} j`,
    n: days,
  })
}

export default ConseillerProPage
